import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Policies from "./pages/Policies";
import ProxyRules from "./pages/ProxyRules";
import Threats from "./pages/Threats";
import SelfHealing from "./pages/SelfHealing";
import AuditLog from "./pages/AuditLog";
import Connectors from "./pages/Connectors";
import Compliance from "./pages/Compliance";
import Vpn from "./pages/Vpn";
import Certs from "./pages/Certs";
import Network from "./pages/Network";
import Profile from "./pages/Profile";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Labels from "./pages/Labels";
import DLPRules from "./pages/DLPRules";
import Classifier from "./pages/Classifier";
import IdentityPolicies from "./pages/IdentityPolicies";
import KMSKeys from "./pages/KMSKeys";
import AuditSinks from "./pages/AuditSinks";
import SSO from "./pages/SSO";
import Orgs from "./pages/Orgs";
import Monitor from "./pages/Monitor";
import PCap from "./pages/PCap";
import Ephemeral from "./pages/Ephemeral";
import QoS from "./pages/QoS";
import SearchFilter from "./pages/SearchFilter";
import Appliances from "./pages/Appliances";
import Telemetry from "./pages/Telemetry";
import Marketing from "./pages/Marketing";
import PlatformAdmin from "./pages/PlatformAdmin";

// Hostname-based routing: rustwall.io apex serves the marketing/signup site;
// admin.rustwall.io serves the platform-admin dashboard; every other hostname
// (tenant subdomains and local dev) gets the normal tenant app.
const HOST = typeof window !== "undefined" ? window.location.hostname : "";
const IS_APEX = HOST === "rustwall.io" || HOST === "www.rustwall.io";
const IS_PLATFORM_ADMIN = HOST === "admin.rustwall.io";

function AppRouter() {
  if (IS_APEX) {
    return (
      <Routes>
        <Route path="/" element={<Marketing />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  if (IS_PLATFORM_ADMIN) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><PlatformAdmin /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute><Policies /></ProtectedRoute>} />
      <Route path="/proxy" element={<ProtectedRoute><ProxyRules /></ProtectedRoute>} />
      <Route path="/threats" element={<ProtectedRoute><Threats /></ProtectedRoute>} />
      <Route path="/healing" element={<ProtectedRoute><SelfHealing /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
      <Route path="/connectors" element={<ProtectedRoute><Connectors /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
      <Route path="/vpn" element={<ProtectedRoute><Vpn /></ProtectedRoute>} />
      <Route path="/certs" element={<ProtectedRoute><Certs /></ProtectedRoute>} />
      <Route path="/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute requireAdmin><Users /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/labels" element={<ProtectedRoute><Labels /></ProtectedRoute>} />
      <Route path="/dlp" element={<ProtectedRoute><DLPRules /></ProtectedRoute>} />
      <Route path="/classifier" element={<ProtectedRoute><Classifier /></ProtectedRoute>} />
      <Route path="/identity-policies" element={<ProtectedRoute><IdentityPolicies /></ProtectedRoute>} />
      <Route path="/kms" element={<ProtectedRoute requireAdmin><KMSKeys /></ProtectedRoute>} />
      <Route path="/audit-sinks" element={<ProtectedRoute requireAdmin><AuditSinks /></ProtectedRoute>} />
      <Route path="/sso" element={<ProtectedRoute requireAdmin><SSO /></ProtectedRoute>} />
      <Route path="/orgs" element={<ProtectedRoute requireAdmin><Orgs /></ProtectedRoute>} />
      <Route path="/monitor" element={<ProtectedRoute><Monitor /></ProtectedRoute>} />
      <Route path="/pcap" element={<ProtectedRoute requireAdmin><PCap /></ProtectedRoute>} />
      <Route path="/ephemeral" element={<ProtectedRoute><Ephemeral /></ProtectedRoute>} />
      <Route path="/qos" element={<ProtectedRoute requireAdmin><QoS /></ProtectedRoute>} />
      <Route path="/search-filter" element={<ProtectedRoute requireAdmin><SearchFilter /></ProtectedRoute>} />
      <Route path="/appliances" element={<ProtectedRoute requireAdmin><Appliances /></ProtectedRoute>} />
      <Route path="/telemetry" element={<ProtectedRoute><Telemetry /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#09090b",
                border: "1px solid #27272a",
                color: "#fafafa",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: "12px",
                borderRadius: "2px",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
